import { useEffect, useState } from "react";
import { createFileRoute, Link, useRouter } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import {
  ChevronRight,
  CreditCard,
  Heart,
  LifeBuoy,
  LogOut,
  Moon,
  Plug,
  Sun,
  Target,
  Trash2,
} from "lucide-react";

import { signOut } from "@/lib/auth";
import { applyTheme, initialTheme, storeTheme, type Theme } from "@/lib/theme";

/**
 * Settings — a menu, not a page of forms.
 *
 * Flat rows on the page ground with hairline rules, rather than cards. Cards
 * were the first attempt and were wrong for this screen: a settings list is
 * one continuous thing to scan, and boxing each group made six objects out of
 * what should read as one column. The grouping survives as space between
 * runs of rows, which is all it needed to be.
 *
 * Nothing here opens a form. Every row is a destination, and the forms live
 * where the thing itself lives — path and interests on the profile, because
 * they are who she is rather than plumbing.
 */
/**
 * `settings.index.tsx`, not `settings.tsx`.
 *
 * A bare `settings.tsx` alongside `settings.connections.tsx` makes the former
 * a *layout* for the latter — and a layout with no `<Outlet />` renders itself
 * for every child route. `/settings/connections` quietly showed the menu
 * instead of the connections page, with no error anywhere. The index suffix
 * makes them siblings.
 */
export const Route = createFileRoute("/settings/")({
  component: SettingsMenu,
});

function SettingsMenu() {
  const router = useRouter();
  const queryClient = useQueryClient();

  /**
   * The theme lives here now.
   *
   * It used to be a floating button pinned to the bottom-right of every
   * screen, which meant a control that is touched roughly twice in a product's
   * lifetime sat permanently on top of the content — and on the interests
   * screen it landed directly over the save button. A preference belongs in
   * the place you go to set preferences.
   */
  const [theme, setTheme] = useState<Theme>("light");
  useEffect(() => {
    setTheme(initialTheme(document.documentElement, safeStorage()));
  }, []);

  function chooseTheme(next: Theme) {
    setTheme(next);
    applyTheme(document.documentElement, next);
    storeTheme(safeStorage(), next);
  }

  /**
   * Clearing the cache is not optional.
   *
   * The SPA caches every answer, and invalidating only marks it stale — so the
   * next person at this browser sees the last one's loop for the frame before
   * the refetch lands. `clear()`, then navigate (AGENTS.md).
   */
  async function leave() {
    await signOut();
    queryClient.clear();
    await router.navigate({ to: "/login", replace: true });
  }

  return (
    <div className="setwrap">
      <header className="sethead">
        <h1 className="settitle">Settings</h1>
      </header>

      <section className="setgroup">
        <Row
          to="/profile"
          icon={<Target size={17} aria-hidden />}
          label="Change my path"
          sub="What Omina ranks every room against"
        />
        <Row
          to="/profile"
          icon={<Heart size={17} aria-hidden />}
          label="Edit my interests"
          sub="The rooms and topics you actually want"
        />
      </section>

      <section className="setgroup">
        <Row
          to="/settings/connections"
          icon={<Plug size={17} aria-hidden />}
          label="Integrations and connections"
          sub="Google, LinkedIn, email and more"
        />
      </section>

      {/* Invoices live inside Subscription rather than beside it — receipts are
          a view of a plan, not a peer of one, and a founder with no plan has no
          invoices to look at. */}
      <section className="setgroup">
        <Row
          to="/settings/billing"
          icon={<CreditCard size={17} aria-hidden />}
          label="Subscription"
          sub="Your plan, payment and invoices"
        />
        <Row
          to="/settings/support"
          icon={<LifeBuoy size={17} aria-hidden />}
          label="Support"
          sub="Get help, or tell us something is wrong"
        />
      </section>

      {/* Appearance is a choice made in place — the only row on this screen
          that does not navigate, because there is nothing behind it to see. */}
      <section className="setgroup">
        <div className="setrow isstatic">
          <span className="seticon">
            {theme === "dark" ? <Moon size={17} aria-hidden /> : <Sun size={17} aria-hidden />}
          </span>
          <span className="settext">
            <b>Appearance</b>
            <span>Paper or midnight</span>
          </span>
          <span className="setseg" role="group" aria-label="Theme">
            <button
              type="button"
              className={theme === "light" ? "on" : undefined}
              aria-pressed={theme === "light"}
              onClick={() => chooseTheme("light")}
            >
              Light
            </button>
            <button
              type="button"
              className={theme === "dark" ? "on" : undefined}
              aria-pressed={theme === "dark"}
              onClick={() => chooseTheme("dark")}
            >
              Dark
            </button>
          </span>
        </div>
      </section>

      <section className="setgroup">
        <Row
          to="/settings/account"
          icon={<Trash2 size={17} aria-hidden />}
          label="Delete my account"
          sub="What goes, and what it cannot undo"
          tone="danger"
        />
      </section>

      <button type="button" className="setout" onClick={() => void leave()}>
        <LogOut size={16} aria-hidden />
        Sign out
      </button>
    </div>
  );
}

function Row({
  to,
  icon,
  label,
  sub,
  tone,
}: {
  to: string;
  icon: React.ReactNode;
  label: string;
  sub: string;
  tone?: "danger";
}) {
  return (
    <Link to={to} className={tone === "danger" ? "setrow isdanger" : "setrow"}>
      <span className="seticon">{icon}</span>
      <span className="settext">
        <b>{label}</b>
        <span>{sub}</span>
      </span>
      <ChevronRight size={17} className="setchev" aria-hidden />
    </Link>
  );
}

/** Accessing localStorage throws outright in some privacy modes — before you
 *  ever call a method on it. Hence the try around the lookup itself. */
function safeStorage(): Storage | undefined {
  try {
    return window.localStorage;
  } catch {
    return undefined;
  }
}
